import { ref } from 'vue'
import { dbApi } from '@/services/dbApiService'
import { createVisibilityPoller, type VisibilityPoller } from '@/utils/visibilityPoller'

// Shared state + poller behind useMaintenance and useDebugMode.
//
// Both flags live in property_settings for the default property. They used to
// poll separately every 30s with two sequential requests each (default
// property, then one settings key) — four requests per 30s per open tab, also
// in hidden tabs. Now: the property id is cached after the first lookup, one
// request reads both keys (key=in.(maintenance,debug)), one poller drives it,
// and the poller pauses while the tab is hidden (re-checks on return).
//
// Fail-safe for both flags: only an explicit successful response with a truthy
// value flips a flag on. Any error / missing row / falsy value → off.

export const DEFAULT_SHELL_FLAGS_INTERVAL_MS = 90_000

export const inMaintenance = ref(false)
export const maintenancePropertyName = ref('')
export const debugEnabled = ref(false)
export const lastCheckedAt = ref<Date | null>(null)

const TRUE_VALUES = new Set(['true', '1', 'yes'])

export function isTruthyValue(v: unknown): boolean {
  if (typeof v !== 'string') return false
  return TRUE_VALUES.has(v.trim().toLowerCase())
}

let cachedProperty: { id: number | string; name: string } | null = null
let inFlight: Promise<void> | null = null
let poller: VisibilityPoller | null = null

async function resolveDefaultProperty() {
  if (cachedProperty) return cachedProperty
  const props = await dbApi.get('/properties?is_default=eq.true&select=id,name&limit=1')
  if (!Array.isArray(props) || props.length === 0) return null
  cachedProperty = { id: props[0].id, name: props[0].name || '' }
  return cachedProperty
}

async function doRefresh() {
  try {
    const property = await resolveDefaultProperty()
    if (!property) {
      inMaintenance.value = false
      maintenancePropertyName.value = ''
      debugEnabled.value = false
      return
    }
    // Secret-free view: V214 revokes browser SELECT on property_settings
    // (T-3870). Both keys are non-secret and pass through unchanged.
    const settings = await dbApi.get(
      `/property_settings_safe?property_id=eq.${property.id}&key=in.(maintenance,debug)&select=key,value`
    )
    const rows: Array<{ key?: string; value?: unknown }> = Array.isArray(settings) ? settings : []
    const flag = (key: string) => rows.some((r) => r?.key === key && isTruthyValue(r?.value))
    const maintenance = flag('maintenance')
    inMaintenance.value = maintenance
    maintenancePropertyName.value = maintenance ? property.name : ''
    debugEnabled.value = flag('debug')
  } catch (err) {
    // Any failure (network, auth refresh, 4xx/5xx) → both off. Drop the cached
    // property so a changed default property is picked up on the next check.
    cachedProperty = null
    console.warn('[shell-flags] check failed; treating maintenance/debug as off:', err)
    inMaintenance.value = false
    maintenancePropertyName.value = ''
    debugEnabled.value = false
  } finally {
    lastCheckedAt.value = new Date()
  }
}

/** One round trip for both flags; concurrent callers share it. */
export function refreshShellFlags(): Promise<void> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => { inFlight = null })
  }
  return inFlight
}

/** Idempotent — a second start() while running keeps the existing poller. */
export function startShellFlagsPolling(intervalMs = DEFAULT_SHELL_FLAGS_INTERVAL_MS) {
  if (poller?.isRunning()) return
  poller = createVisibilityPoller(() => refreshShellFlags(), intervalMs)
  poller.start()
}

export function stopShellFlagsPolling() {
  poller?.stop()
  poller = null
}
